import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Trash2, Lock } from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import type { Project, GridColumn, GridRow } from '../types';
import { ProjectTimeline } from '../components/ProjectTimeline';
import { useT } from '../i18n';
import { FilterSelect } from '../components/FilterSelect';
import { projectLinksFor } from '../utils/projectLinks';
import type { ProjectLinkView } from '../utils/projectLinks';

// ── Helpers ──

function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

function parseSponsor(p: Project): string {
  return parseJSON<any>(p.ownership, {}).sponsor || '—';
}

function parseUserGroup(p: Project): string {
  const ug = parseJSON<any>(p.ownership, {}).user_group;
  if (!ug) return '—';
  if (Array.isArray(ug)) return ug.join(', ') || '—';
  return ug;
}

function parseEffort(p: Project): string {
  return parseJSON<any>(p.resources_effort, {}).effort_size || '';
}

function parseTimeline(p: Project): string {
  return parseJSON<any>(p.resources_effort, {}).timeline || '';
}

function parseCollaborators(p: Project): { name: string; role: string; allocation: string }[] {
  return parseJSON<any>(p.resources_effort, {}).collaborators || [];
}

// Deliverables are {id, title, body} cards; legacy rows may still be a plain
// string array (kept working here — only the title is shown in this summary).
function parseDeliverables(p: Project): { id: string; title: string }[] {
  const arr = parseJSON<any[]>(p.deliverables, []);
  return arr.map((d, i) => typeof d === 'string' ? { id: d, title: d } : { id: d.id || `d${i}`, title: d.title || '' });
}

function parseValueAdded(p: Project): string[] {
  return parseJSON<string[]>(p.value_added, []);
}

function parseInfrastructure(p: Project): string[] {
  return parseJSON<any>(p.dependencies, {}).infrastructure || [];
}

// ── Detail Panel ──

function DetailPanel({ project, projects, onClose }: {
  project: Project;
  projects: Project[];
  onClose: () => void;
}) {
  const { t } = useT();
  const { blockers, blocked } = useMemo(() => projectLinksFor(project, projects), [project, projects]);

  return (
    <div className={`panel-overlay open`} onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" onClick={e => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{project.name}</div>
          <button className="panel-slide-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="panel-slide-body">
              {/* Status / Domain / Level badges */}
              <div className="detail-badges">
                {project.status_label && <span className="badge badge-accent">{project.status_label}</span>}
                {project.domain_label && <span className="badge">{project.domain_label}</span>}
                {project.level_label && <span className="badge">{project.level_label}</span>}
              </div>

              {/* Problem */}
              {project.problem && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.problem')}</div>
                  <div className="detail-text">{project.problem}</div>
                </div>
              )}

              {/* Goal */}
              {project.goal && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.goal')}</div>
                  <div className="detail-text">{project.goal}</div>
                </div>
              )}

              {/* Value Added */}
              {parseValueAdded(project).length > 0 && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.valueAdded')}</div>
                  <div className="detail-badges">
                    {parseValueAdded(project).map(v => <span key={v} className="badge">{v}</span>)}
                  </div>
                </div>
              )}

              {/* Deliverables */}
              {parseDeliverables(project).length > 0 && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.deliverables')}</div>
                  <ul className="detail-list">
                    {parseDeliverables(project).map((d) => <li key={d.id}>{d.title}</li>)}
                  </ul>
                </div>
              )}

              {/* Resources & Effort */}
              {(parseEffort(project) || parseTimeline(project) || parseCollaborators(project).length > 0) && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.resourcesEffort')}</div>
                  {parseEffort(project) && <div className="detail-row"><span className="detail-row-label">{t('list.effort')}</span> {parseEffort(project)}</div>}
                  {parseTimeline(project) && <div className="detail-row"><span className="detail-row-label">{t('list.timeline')}</span> {parseTimeline(project)}</div>}
                  {parseCollaborators(project).length > 0 && (
                    <div className="detail-collabs">
                      {parseCollaborators(project).map((c, i) => (
                        <div key={i} className="detail-collab">
                          <span>{c.name}</span>
                          {c.allocation && <span className="text-ink-3">{c.allocation}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Ownership */}
              <div className="detail-section">
                <div className="detail-label">{t('list.ownership')}</div>
                <div className="detail-row"><span className="detail-row-label">{t('list.sponsor')}</span> {parseSponsor(project)}</div>
                <div className="detail-row"><span className="detail-row-label">{t('list.userGroup')}</span> {parseUserGroup(project)}</div>
              </div>

              {/* Dependencies */}
              {(blockers.length > 0 || blocked.length > 0 || parseInfrastructure(project).length > 0) && (
                <div className="detail-section">
                  <div className="detail-label">{t('list.dependencies')}</div>
                  {blockers.length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('list.enabledBy')}</div>
                      {blockers.map((l: ProjectLinkView, i: number) => <div key={i} className="detail-dep-item" title={l.reason || undefined}>{l.name}</div>)}
                    </div>
                  )}
                  {blocked.length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('list.enables')}</div>
                      {blocked.map((l: ProjectLinkView, i: number) => <div key={i} className="detail-dep-item" title={l.reason || undefined}>{l.name}</div>)}
                    </div>
                  )}
                  {parseInfrastructure(project).length > 0 && (
                    <div className="detail-dep-group">
                      <div className="detail-row-label">{t('list.infrastructure')}</div>
                      <div className="detail-badges">
                        {parseInfrastructure(project).map((inf, i) => <span key={i} className="badge">{inf}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* The history is for the people who are part of the project; others see its overview or card only. */}
              {(project.access_level ?? 'full') === 'full'
                ? <ProjectTimeline projectId={project.id} />
                : <div className="detail-section text-sm text-ink-3">{t(project.access_level === 'card' ? 'projectDetail.privateCardOnly' : 'projectDetail.overviewOnly')}</div>}
        </div>
      </div>
    </div>
  );
}

// ── List Page ──

export function ListPage() {
  const { t } = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  const [domains, setDomains] = useState<GridColumn[]>([]);
  const [levels, setLevels] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<number | null>(null);
  const [levelFilter, setLevelFilter] = useState<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number } | null>(null);

  const loadData = useCallback(async () => {
    const [p, d, l] = await Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
    ]);
    setProjects(p);
    setDomains(d);
    setLevels(l);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget({ id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await api.deleteProject(deleteTarget.id);
    setDeleteTarget(null);
    setSelectedProject(null);
    loadData();
  };

  const filtered = useMemo(() => {
    let result = projects;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        parseSponsor(p).toLowerCase().includes(q) ||
        parseUserGroup(p).toLowerCase().includes(q)
      );
    }
    if (domainFilter !== null) {
      result = result.filter(p => p.domain_id === domainFilter);
    }
    if (levelFilter !== null) {
      result = result.filter(p => p.level_id === levelFilter);
    }
    return result;
  }, [projects, search, domainFilter, levelFilter]);

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('list.projects')}</h1>
          <p className="page-meta">{filtered.length === 1 ? t('list.projectCountOne', { n: filtered.length }) : t('list.projectCountOther', { n: filtered.length })}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="filter-chips">
          <button
            className={`chip ${domainFilter === null && levelFilter === null ? 'active' : ''}`}
            onClick={() => { setDomainFilter(null); setLevelFilter(null); }}
          >
            {t('common.all')}
          </button>
          {levels.map(l => (
            <button
              key={l.id}
              className={`chip ${levelFilter === l.id ? 'active' : ''}`}
              onClick={() => setLevelFilter(levelFilter === l.id ? null : l.id)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <FilterSelect
          label={t('filters.domain')}
          multiple={false}
          selected={domainFilter != null ? [domainFilter] : []}
          options={domains.map(d => ({ value: d.id, label: d.label }))}
          onChange={vals => setDomainFilter(vals[0] ?? null)}
        />
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('list.searchProjects')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('list.noProjectsTitle')}</div>
          <div className="empty-state-desc">{t('list.noProjectsDesc')}</div>
        </div>
      ) : (
        <table className="table table-sticky">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('list.sponsor')}</th>
              <th>{t('list.userGroup')}</th>
              <th>{t('list.domain')}</th>
              <th>{t('list.level')}</th>
              <th>{t('common.status')}</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr
                key={p.id}
                className={`list-row-clickable ${selectedProject?.id === p.id ? 'list-row-selected' : ''}`}
                onClick={() => setSelectedProject(p)}
              >
                <td>
                  <span
                    className="font-medium text-ink-1"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.hash = `project?id=${p.id}`;
                    }}
                  >
                    {p.is_private && <span title={t('projects.privateBadge')} style={{ display: 'inline-flex' }}><Lock size={11} style={{ marginRight: 4, opacity: 0.5, flexShrink: 0 }} /></span>}
                    {p.name}
                  </span>
                </td>
                <td className="text-sm">{parseSponsor(p)}</td>
                <td className="text-sm">{parseUserGroup(p)}</td>
                <td>{p.domain_label ? <span className="badge">{p.domain_label}</span> : <span className="text-ink-3">—</span>}</td>
                <td>{p.level_label ? <span className="badge">{p.level_label}</span> : <span className="text-ink-3">—</span>}</td>
                <td>{p.status_label ? <span className="badge badge-accent">{p.status_label}</span> : '—'}</td>
                <td>
                  <button className="btn-icon" title={t('common.delete')} onClick={(e) => handleDelete(p.id, e)}>
                    <Trash2 className="icon-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Slide-in detail panel */}
      {selectedProject && (
        <DetailPanel
          project={selectedProject}
          projects={projects}
          onClose={() => setSelectedProject(null)}
        />
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('list.deleteProjectTitle')}
        message={t('list.deleteProjectMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
